// The matter's Drive folder as the folder actually is: every file in it,
// which of them this matter already holds, and which can never be pulled in
// at all. Import runs one file at a time - each one is a download and an
// extraction - so the row that is working says so.

import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "../chrome/Dialog";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import type { CaseDriveFile, ImportBlocker } from "@duralex/shared";
import { fetchCaseDriveFolder, importDriveFile } from "../../api/drive";

const BLOCKED_WHY: Record<ImportBlocker, { label: string; title: string }> = {
  "case-info": {
    label: "Folder metadata",
    title: "The folder's own _case-info.json, not a document of the matter.",
  },
  "google-native": {
    label: "Google Doc, no file",
    title: "A Google Doc, Sheet or Slide has no file to download - export it to PDF first.",
  },
  "too-large": {
    label: "Over 10 MB",
    title: "Larger than the 10 MB import limit.",
  },
  "render-artifact": {
    label: "Generator leftover",
    title: "A working file the document generator left beside the paper it rendered.",
  },
  "intake-record": {
    label: "Intake record",
    title:
      "The questionnaire's own record and memos, written by DuraLex at intake - the answers are already on the matter.",
  },
};

// A reason this page does not know is a page older than the server - a tab
// left open across a deploy. Reading it blind threw, and with no error
// boundary in the app the whole matter went blank.
function blockedWhy(blocked: ImportBlocker) {
  return (
    BLOCKED_WHY[blocked] ?? {
      label: "Cannot be imported",
      title: "The server gave a reason this page does not know - reload to get the current one.",
    }
  );
}

function fmtSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

interface Props {
  caseId: string;
  open: boolean;
  onClose: () => void;
}

interface RowResult {
  ok: boolean;
  text: string;
}

export function DriveFolderDialog({ caseId, open, onClose }: Props) {
  const folderQ = useQuery({
    queryKey: ["drive-folder", caseId],
    queryFn: () => fetchCaseDriveFolder(caseId),
    // The dialog above stays mounted while closed, so the request has to be
    // gated here or every matter would read Drive on render.
    enabled: open,
    staleTime: 30_000,
    retry: false,
  });
  const queryClient = useQueryClient();
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const [working, setWorking] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const stopped = useRef(false);

  const files = folderQ.data?.files ?? [];
  const pending = files.filter((f) => !f.blocked && !f.alreadyHere && !results[f.id]?.ok);
  const running = progress !== null;

  async function runImport(list: CaseDriveFile[]) {
    stopped.current = false;
    setProgress({ done: 0, total: list.length });
    for (const [i, f] of list.entries()) {
      if (stopped.current) break;
      setWorking(f.id);
      try {
        const r = await importDriveFile(f.id, caseId);
        setResults((p) => ({ ...p, [f.id]: { ok: true, text: r.message } }));
      } catch (err) {
        setResults((p) => ({
          ...p,
          [f.id]: { ok: false, text: err instanceof Error ? err.message : "Import failed" },
        }));
      }
      setProgress({ done: i + 1, total: list.length });
    }
    setWorking(null);
    setProgress(null);
    await queryClient.invalidateQueries({ queryKey: ["matter"] });
    await folderQ.refetch();
  }

  function close() {
    // The files already sent keep going on the server; what stops is asking
    // for more of them from a dialog nobody is watching.
    stopped.current = true;
    onClose();
  }

  return (
    <Dialog open={open} onClose={close} title="Drive folder" locked={running}>
      {folderQ.isLoading && (
        <SkeletonGroup>
          <Skeleton width={260} />
          <Skeleton width={200} />
        </SkeletonGroup>
      )}
      {folderQ.isError && <p className="drive-dlg-error">{(folderQ.error as Error).message}</p>}

      {folderQ.data && !folderQ.data.linked && (
        <p className="drive-dlg-note">
          This matter has no Drive folder linked, so there is nothing to pull in.
        </p>
      )}

      {folderQ.data?.unreachable && (
        <p className="drive-dlg-note">
          The folder on this matter could not be read. It may have been trashed or moved out of the
          firm's Shared Drive.
        </p>
      )}

      {folderQ.data?.linked && !folderQ.data.unreachable && (
        <>
          <div className="drive-dlg-head">
            <p className="drive-dlg-note">
              {files.length} file{files.length === 1 ? "" : "s"} in the folder
              {pending.length > 0 ? `, ${pending.length} not in this matter yet` : ", all here"}.
              {(folderQ.data.subfolders ?? 0) > 0 &&
                ` Files from ${folderQ.data.subfolders} subfolder${folderQ.data.subfolders === 1 ? "" : "s"} are included.`}
            </p>
            {pending.length > 0 && (
              <button
                type="button"
                className="drive-dlg-run"
                disabled={running}
                onClick={() => runImport(pending)}
              >
                {progress
                  ? `Importing ${Math.min(progress.done + 1, progress.total)} of ${progress.total}...`
                  : `Import ${pending.length}`}
              </button>
            )}
          </div>

          {files.length === 0 && <p className="drive-dlg-note">The folder is empty.</p>}

          <ul className="drive-dlg-list">
            {files.map((f) => {
              const result = results[f.id];
              return (
                <li key={f.id} className="drive-dlg-row">
                  <span
                    className="name"
                    title={f.subfolder ? `${f.subfolder} / ${f.name}` : f.name}
                  >
                    <span className="file">{f.name}</span>
                    {f.subfolder ? <span className="sub">{f.subfolder}</span> : null}
                  </span>
                  <span className="size">{fmtSize(f.size)}</span>
                  {f.blocked ? (
                    <span className="state muted" title={blockedWhy(f.blocked).title}>
                      {blockedWhy(f.blocked).label}
                    </span>
                  ) : result ? (
                    <span className={result.ok ? "state ok" : "state bad"}>{result.text}</span>
                  ) : f.alreadyHere ? (
                    <span className="state muted">In this matter</span>
                  ) : working === f.id ? (
                    <span className="state">Importing...</span>
                  ) : (
                    <button
                      type="button"
                      className="drive-dlg-one"
                      disabled={running}
                      onClick={() => runImport([f])}
                    >
                      Import
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          {running && (
            <button
              type="button"
              className="drive-dlg-stop"
              onClick={() => (stopped.current = true)}
            >
              Stop after this file
            </button>
          )}
        </>
      )}
    </Dialog>
  );
}
